/**
 * Tenant Management Service
 * Canonical implementation for tenant administration
 */

import { buildTenantUrl } from "../../config/services-config";
import type { HttpClient } from "../client";
import type {
	InviteTeamMemberRequest,
	TeamMember,
	Tenant,
	UpdateTeamMemberRequest,
} from "../types";

export class TenantService {
	private httpClient: HttpClient;

	constructor(httpClient: HttpClient) {
		this.httpClient = httpClient;
	}

	// ==================== Tenant Information ====================

	async getTenantInfo(tenantSlug: string): Promise<Tenant> {
		const url = buildTenantUrl(tenantSlug, "/info");
		return await this.httpClient.get<Tenant>(url);
	}

	async updateTenant(
		tenantSlug: string,
		updates: Partial<Tenant>,
	): Promise<Tenant> {
		const url = buildTenantUrl(tenantSlug, "/settings");
		return await this.httpClient.patch<Tenant>(url, updates);
	}

	// ==================== Team Management ====================

	async getTeamMembers(tenantSlug: string): Promise<TeamMember[]> {
		const url = buildTenantUrl(tenantSlug, "/admin/team");
		const response = await this.httpClient.get<{ members: TeamMember[] }>(url);
		return response.members;
	}

	async inviteTeamMember(
		tenantSlug: string,
		request: InviteTeamMemberRequest,
	): Promise<void> {
		const url = buildTenantUrl(tenantSlug, "/admin/team/invite");
		await this.httpClient.post<void>(url, request);
	}

	async removeTeamMember(tenantSlug: string, memberId: string): Promise<void> {
		const url = buildTenantUrl(tenantSlug, `/admin/team/${memberId}`);
		await this.httpClient.delete<void>(url);
	}

	async updateTeamMemberRole(
		tenantSlug: string,
		memberId: string,
		request: UpdateTeamMemberRequest,
	): Promise<void> {
		const url = buildTenantUrl(tenantSlug, `/admin/team/${memberId}`);
		await this.httpClient.patch<void>(url, request);
	}

	// ==================== Analytics ====================

	async getAnalytics(
		tenantSlug: string,
		timeframe: string = "7d",
	): Promise<any> {
		const url = buildTenantUrl(
			tenantSlug,
			`/admin/analytics?timeframe=${timeframe}`,
		);
		return await this.httpClient.get<any>(url);
	}

	async getUsageStats(tenantSlug: string): Promise<any> {
		const url = buildTenantUrl(tenantSlug, "/admin/usage");
		return await this.httpClient.get<any>(url);
	}

	// ==================== Billing (placeholder endpoints) ====================

	async getBillingInfo(tenantSlug: string): Promise<any> {
		const url = buildTenantUrl(tenantSlug, "/admin/billing");
		return await this.httpClient.get<any>(url);
	}

	async updateBillingInfo(tenantSlug: string, billingData: any): Promise<void> {
		const url = buildTenantUrl(tenantSlug, "/admin/billing");
		await this.httpClient.patch<void>(url, billingData);
	}

	// ==================== Webhooks ====================

	async getWebhooks(tenantSlug: string): Promise<any[]> {
		const url = buildTenantUrl(tenantSlug, "/admin/webhooks");
		const response = await this.httpClient.get<{ webhooks: any[] }>(url);
		return response.webhooks || [];
	}

	async createWebhook(tenantSlug: string, webhook: any): Promise<any> {
		const url = buildTenantUrl(tenantSlug, "/admin/webhooks");
		return await this.httpClient.post<any>(url, webhook);
	}

	async updateWebhook(
		tenantSlug: string,
		webhookId: string,
		updates: any,
	): Promise<any> {
		const url = buildTenantUrl(tenantSlug, `/admin/webhooks/${webhookId}`);
		return await this.httpClient.patch<any>(url, updates);
	}

	async deleteWebhook(tenantSlug: string, webhookId: string): Promise<void> {
		const url = buildTenantUrl(tenantSlug, `/admin/webhooks/${webhookId}`);
		await this.httpClient.delete<void>(url);
	}
}
